/**
 * Profile Info Section
 *
 * Displays and allows editing of user profile information.
 * Shows: Name, Email, Role, Status, Account/Workspace info.
 */

import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Group, Login, Tag, TagType, UserRole, UserTag, Workspace } from '../../apis/api-client';
import { authClient, groupsClient, tagsClient, usersClient, workspacesClient } from '../../apis/client-instances';
import { updateProfile } from '../../features/profile/profileSlice';
import { alert, getFileUrl, uploadFile } from '../../hooks/function';
import { RootState } from '../../types';
import { formatDateTime, getStatusColor, getUserStatus, isAccountOrSystemAdmin, isAdmin } from '../../utils/roleHelpers';
import { canEditUserTags, isTagValidForUser, tagScopeForUser } from '../../utils/tagFilters';
import { validateEmail } from '../../utils/validation';
import { LicenseDraft, apiLicenseToDraft, draftToLicenseItem } from '../../utils/licenses';
import { learnerProfileClient, referenceFieldsClient } from '../../apis/client-instances';
import { selectAllProfessions, useEnsureProfessions } from '../../features/lookups/lookupsSlice';
import LearnerLicensesSection from './LearnerLicensesSection';
import UserReferenceFieldsSection from './UserReferenceFieldsSection';
import ProfileIdentityCard from './profileInfo/ProfileIdentityCard';
import ProfileAccessCard from './profileInfo/ProfileAccessCard';
import ProfilePasswordCard from './profileInfo/ProfilePasswordCard';
import { SettingsCard } from '../common';
import { Box, Button, CircularProgress, Typography } from '../ui';

interface ProfileInfoSectionProps {
  user: Login;
  currentUserId?: number;
  currentUserRole?: any; // UserRole from api-client
  onUpdate?: () => void;
}

export default function ProfileInfoSection({ user, currentUserId, currentUserRole, onUpdate }: ProfileInfoSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [isActive, setIsActive] = useState(user.isActive ?? true);
  const [formData, setFormData] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    role: user.role || UserRole.User,
    workspaceId: user.workspaceId as number | undefined | null,
    jobTitle: user.jobTitle || '',
    profileImageUrl: user.profileImageUrl || '',
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [resolvedImageUrl, setResolvedImageUrl] = useState<string | null>(null);
  const [photoSaving, setPhotoSaving] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState<any>({});
  const [validationErrors, setValidationErrors] = useState<any>({});
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  // Groups the user is currently a member of, scoped to their
  // workspace. Tracked alongside the original snapshot so the save
  // flow can diff and fire add/remove against just the changed ids
  // (the membership API has no bulk-replace endpoint).
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [originalGroupIds, setOriginalGroupIds] = useState<number[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  // Tracks the resolved Workspace object so the autocomplete can display the
  // user's currently-assigned workspace name. Only the id is submitted.
  const [resolvedWorkspace, setResolvedWorkspace] = useState<Workspace | null>(null);

  // Get current user info from Redux
  const currentUser = useSelector((state: RootState) => state.profile.user);

  // Get account name from Redux (for displaying instead of "Account Level")
  const accountName = useSelector((state: RootState) => state.account.name);

  const isOwnProfile = currentUserId === user.id;

  // Workspace-level setting: when a learner is viewing their own
  // profile, the workspace admin can opt them in to editing their own
  // info. Read off the loaded workspace (`resolvedWorkspace`, fetched
  // below for the workspace autocomplete); a learner is allowed to read
  // their own workspace server-side. Default locked until it resolves
  // and only relevant for one's own profile (admins always can edit).
  const workspaceSelfEditAllowed =
    isOwnProfile && resolvedWorkspace?.allowLearnerSelfEdit === true;
  // Admins can always edit. Learners viewing their own profile can edit
  // only when the workspace setting unlocks it.
  const canEdit = isAdmin(currentUserRole) || (isOwnProfile && workspaceSelfEditAllowed);

  // Profession + licenses. The profession list is account-scoped
  // reference data (lookups slice); the user's selection and licenses
  // come off LearnerProfileClient. Both load per-user and save in the
  // single Save Changes flow below.
  useEnsureProfessions();
  const professions = useSelector(selectAllProfessions);
  const [professionId, setProfessionId] = useState<number | null>(null);
  const [licenses, setLicenses] = useState<LicenseDraft[]>([]);
  useEffect(() => {
    if (user?.id == null) {
      setProfessionId(null);
      setLicenses([]);
      return;
    }
    let cancelled = false;
    Promise.all([
      learnerProfileClient.getProfession(Number(user.id)).catch(() => null),
      learnerProfileClient.getLicenses(Number(user.id)).catch(() => []),
    ]).then(([profId, lics]) => {
      if (cancelled) return;
      setProfessionId(profId ?? null);
      setLicenses((lics ?? []).map(apiLicenseToDraft));
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Reference field values for this user (keyed by fieldId). Staged in
  // component state so the parent's Save Changes flow writes them alongside
  // the standard profile fields — admins don't have to remember a second save.
  const [referenceValues, setReferenceValues] = useState<Record<string, string>>({});
  useEffect(() => {
    if (user?.id == null) {
      setReferenceValues({});
      return;
    }
    let cancelled = false;
    referenceFieldsClient
      .getValues(Number(user.id))
      .then((rows) => {
        if (cancelled) return;
        setReferenceValues(
          Object.fromEntries(rows.map((r) => [String(r.fieldId), r.value ?? ''])),
        );
      })
      .catch(() => {
        if (!cancelled) setReferenceValues({});
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);
  // Hidden for learners viewing their own profile — these identifiers
  // are admin-managed integration mappings, not learner-editable data.
  const showReferenceFields = isAdmin(currentUserRole);
  // Schema management belongs to the highest roles; workspace admins
  // can fill values for their users but the field LIST is a tenant
  // concern.
  const canManageReferenceSchema = isAccountOrSystemAdmin(currentUserRole);

  // Separate permission for tag editing
  const canEditTags = canEditUserTags(currentUserRole as UserRole);

  /**
   * Check if current user can edit this user's role
   */
  const canEditRole = (): boolean => {
    if (!currentUserRole || isOwnProfile) return false;

    // Admin can edit anyone except other Admins
    if (currentUserRole === UserRole.Admin && user.role !== UserRole.Admin) {
      return true;
    }

    // AccountAdmin can edit User, WorkspaceAdmin, AccountAdmin
    if (currentUserRole === UserRole.AccountAdmin && user.role !== UserRole.Admin) {
      return true;
    }

    // WorkspaceAdmin can edit User and WorkspaceAdmin in their workspace
    if (
      currentUserRole === UserRole.WorkspaceAdmin &&
      (user.role === UserRole.User || user.role === UserRole.WorkspaceAdmin) &&
      user.workspaceId === currentUser?.workspaceId
    ) {
      return true;
    }

    return false;
  };

  /**
   * Check if current user can edit this user's workspace
   */
  const canEditWorkspace = (): boolean => {
    if ([UserRole.User, UserRole.WorkspaceAdmin].includes(currentUser?.role))
      return false;
    // Same permission as editing role
    return canEditRole();
  };

  /**
   * Filter available role options based on current user's permissions
   */
  const availableRoleOptions = useMemo(() => {
    // Labels follow the Role Model in CLAUDE.md — the API enum name
    // `AccountAdmin` is legacy for the Account Admin role.
    const allRoles = [
      { label: 'Learner', value: UserRole.User },
      { label: 'Workspace Admin', value: UserRole.WorkspaceAdmin },
      { label: 'Account Admin', value: UserRole.AccountAdmin },
      { label: 'Super Admin', value: UserRole.Admin },
    ];

    if (!canEditRole()) return allRoles;

    // Admin can change to any role except Admin (can't create new Admins)
    if (currentUserRole === UserRole.Admin) {
      // If target user is Admin, don't allow role change
      if (user.role === UserRole.Admin) {
        return [{ label: 'Super Admin', value: UserRole.Admin }];
      }
      return allRoles;
    }

    // AccountAdmin can assign User, WorkspaceAdmin, AccountAdmin (not Admin)
    if (currentUserRole === UserRole.AccountAdmin) {
      return allRoles.filter((role) => role.value !== UserRole.Admin);
    }

    // WorkspaceAdmin can only assign User and WorkspaceAdmin
    if (currentUserRole === UserRole.WorkspaceAdmin) {
      return allRoles.filter((role) => role.value === UserRole.User || role.value === UserRole.WorkspaceAdmin);
    }

    return allRoles;
  }, [currentUserRole, user.role, canEditRole]);

  // Update form data when user changes
  useEffect(() => {
    setFormData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      role: user.role || UserRole.User,
      workspaceId: user.workspaceId,
      jobTitle: user.jobTitle || '',
      profileImageUrl: user.profileImageUrl || '',
    });
  }, [user]);

  // Resolve the saved profile image filename → SAS-signed URL for display.
  // Skipped when imagePreview is set (user has selected a new file but not saved yet).
  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      if (imagePreview) return;
      if (!user.profileImageUrl) {
        setResolvedImageUrl(null);
        return;
      }
      try {
        const url = await getFileUrl(user.profileImageUrl);
        if (!cancelled) setResolvedImageUrl(url || null);
      } catch {
        if (!cancelled) setResolvedImageUrl(null);
      }
    };
    resolve();
    return () => { cancelled = true; };
  }, [user.profileImageUrl, imagePreview]);

  // Resolve the currently-assigned workspace for display in the autocomplete.
  useEffect(() => {
    if (!formData.workspaceId) {
      setResolvedWorkspace(null);
      return;
    }
    let cancelled = false;
    workspacesClient
      .getWorkspaceById(formData.workspaceId)
      .then((w) => {
        if (!cancelled) setResolvedWorkspace(w ?? null);
      })
      .catch(() => {
        if (!cancelled) setResolvedWorkspace({ id: formData.workspaceId } as Workspace);
      });
    return () => {
      cancelled = true;
    };
  }, [formData.workspaceId]);

  // Fetch user's current tags
  useEffect(() => {
    const fetchUserTags = async () => {
      try {
        setTagsLoading(true);

        if (user.id) {
          const userTags = user?.tags;
          const userTagObjects = userTags.map((ut: UserTag) => ut).filter((tag) => !!tag);
          setSelectedTags(userTagObjects);
        }
      } catch (error: any) {
        console.error('Failed to fetch user tags:', error);
      } finally {
        setTagsLoading(false);
      }
    };

    fetchUserTags();
  }, [user.id, user]);

  // Hydrate the user's current group memberships from the server.
  // `getUserGroups` returns the join-row shape (`UserGroup`) — we cast
  // each to a thin Group shell carrying just id + name, which is all
  // the autocomplete's `isOptionEqualToValue` and the chip label need.
  // Refetches when the user changes (admin clicking through learners).
  useEffect(() => {
    let cancelled = false;
    if (!user?.id) {
      setSelectedGroups([]);
      setOriginalGroupIds([]);
      return;
    }
    setGroupsLoading(true);
    groupsClient
      .getUserGroups(user.id)
      .then((memberships) => {
        if (cancelled) return;
        const shells: Group[] = (memberships ?? [])
          .filter((m) => !!m.groupId)
          .map((m) => ({ id: m.groupId, name: m.groupName } as Group));
        setSelectedGroups(shells);
        setOriginalGroupIds(
          shells.map((g) => g.id).filter((id): id is number => id !== undefined),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setSelectedGroups([]);
        setOriginalGroupIds([]);
      })
      .finally(() => {
        if (!cancelled) setGroupsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Persist a photo change immediately — one path for everyone, no
  // staging and no separate Save button. Uploads the blob (when a new
  // file was picked, `file === null` clears it) then writes it via
  // updateUser: the server's self-branch lets any user set their OWN
  // photo, while admins editing another user hit the managed branch.
  // The non-photo fields are sent from the persisted record (user.*),
  // never the in-progress form, so a photo change never commits an
  // unsaved name/email edit — and never wipes a field the server sets
  // unconditionally.
  const persistPhoto = async (file: File | null) => {
    try {
      setPhotoSaving(true);
      let finalProfileImageUrl = '';
      if (file) {
        const result = await uploadFile(file);
        if (!result?.success) {
          alert(enqueueSnackbar, 'Failed to upload profile image', 'error');
          setImagePreview(null);
          return;
        }
        finalProfileImageUrl = result.uploadedUrl || '';
      }

      await usersClient.updateUser(user.id!, {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.role,
        workspaceId: user.workspaceId,
        jobTitle: user.jobTitle || undefined,
        profileImageUrl: finalProfileImageUrl,
      });

      // Keep the topbar/sidebar avatar in sync when it's our own photo.
      if (isOwnProfile) {
        dispatch(updateProfile({ profileImageUrl: finalProfileImageUrl }));
      }
      setImagePreview(null);
      setFormData((prev) => ({ ...prev, profileImageUrl: finalProfileImageUrl }));
      alert(enqueueSnackbar, file ? 'Profile photo updated' : 'Profile photo removed', 'success');
      onUpdate?.();
    } catch (error: any) {
      // Roll back the optimistic preview on failure.
      setImagePreview(null);
      alert(enqueueSnackbar, error?.response?.data || 'Failed to update photo', 'error');
    } finally {
      setPhotoSaving(false);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so picking the same file again still fires onChange.
    e.target.value = '';
    if (!file) return;
    // Optimistic preview while the upload/save runs.
    setImagePreview(URL.createObjectURL(file));
    persistPhoto(file);
  };

  const handleImageRemove = () => {
    // Drop the resolved URL up-front so the avatar falls back to the
    // placeholder at once, then clear it server-side.
    setResolvedImageUrl(null);
    persistPhoto(null);
  };

  const handleChange = (field: string, value: any) => {
    const newFormData = { ...formData, [field]: value };
    const newErrors = { ...validationErrors, [field]: '' };

    // Clear workspace when changing to Account Admin or Admin role
    if (field === 'role' && (value === UserRole.AccountAdmin || value === UserRole.Admin)) {
      newFormData.workspaceId = null;
      newErrors.workspaceId = '';
    }

    // Workspace change invalidates the current group selections — groups
    // are workspace-scoped, so the chips would point at the WRONG
    // workspace's groups once the form's `workspaceId` flips. Clear
    // them so the admin re-picks against the new workspace's list.
    // (Original ids stay intact so the save diff still removes the
    // stale memberships server-side.)
    if (field === 'workspaceId' && value !== formData.workspaceId) {
      setSelectedGroups([]);
    }

    // Drop selected tags that aren't valid for the new role/workspace combo.
    // Server enforces this on save, but pruning the chips up-front avoids
    // showing the user stale picks.
    if (field === 'role' || field === 'workspaceId') {
      setSelectedTags((prev) =>
        prev.filter((t) => isTagValidForUser(t, newFormData.role, newFormData.workspaceId))
      );
    }

    setFormData(newFormData);
    setValidationErrors(newErrors);
  };

  const validateForm = () => {
    const errors: any = {};

    if (!formData.firstName?.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName?.trim()) {
      errors.lastName = 'Last name is required';
    }

    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      errors.email = emailValidation.error;
    }

    // WorkspaceAdmin MUST have workspace
    if (formData.role === UserRole.WorkspaceAdmin && !formData.workspaceId) {
      errors.workspaceId = 'Workspace is required for Workspace Admin role';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    // Validate form before saving
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // The photo is owned entirely by the upload handler (persistPhoto) —
      // this form save doesn't change it. We still echo the current value
      // so this write preserves it, since the server sets ProfileImageUrl
      // unconditionally on updateUser.
      await usersClient.updateUser(user.id!, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        role: formData.role,
        workspaceId: formData.workspaceId,
        jobTitle: formData.jobTitle || undefined,
        profileImageUrl: formData.profileImageUrl || undefined,
      });

      // Persist profession + licenses alongside the rest of the
      // profile. saveLicenses is a bulk replace; we only send licenses
      // when the selected profession actually tracks licensure (a
      // non-licensed profession clears them server-side). The reconciled
      // rows come back with server ids — adopt them so a second save is
      // a clean update, not a re-insert.
      if (user?.id) {
        await learnerProfileClient.setProfession(Number(user.id), { professionId: professionId ?? undefined });
        const profIsLicensed =
          professions.find((p) => p.id === professionId)?.isLicensed === true;
        const saved = await learnerProfileClient.saveLicenses(Number(user.id), {
          licenses: profIsLicensed ? licenses.map(draftToLicenseItem) : [],
        });
        setLicenses((saved ?? []).map(apiLicenseToDraft));
      }

      // Persist reference field values via the API (one setValue per field).
      // Admin-only; gated above on `showReferenceFields` so the section isn't
      // mounted for learners and `referenceValues` never carries their data
      // into this write.
      if (user?.id && showReferenceFields) {
        await Promise.all(
          Object.entries(referenceValues).map(([fieldId, value]) =>
            referenceFieldsClient.setValue(Number(user.id), {
              fieldId: Number(fieldId),
              value,
            }),
          ),
        );
      }

      // If editing own profile, push the updated bits into Redux so the
      // topbar avatar / name reflect the change without a page reload.
      if (isOwnProfile) {
        // profileImageUrl is dispatched by persistPhoto, not here — the
        // photo saves the moment it's uploaded, not with this form.
        dispatch(updateProfile({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          jobTitle: formData.jobTitle,
        }));
      }

      // Diff the group memberships and fire add/remove for just the
      // changes. The API has no bulk-replace endpoint, so this is the
      // pattern. Failures are swallowed-per-id so a single 403 on one
      // group doesn't lose every other update.
      if (user?.id) {
        await saveGroupMemberships();
      }

      alert(enqueueSnackbar, 'Profile updated successfully', 'success');
      onUpdate?.();
    } catch (error: any) {
      // Handle server errors
      const errorMessage = error?.response?.data;
      if (typeof errorMessage === 'object' && errorMessage !== null) {
        // If server returns field-specific errors, map them to validation errors
        setValidationErrors(errorMessage);
      }
      alert(enqueueSnackbar, typeof errorMessage === 'string' ? errorMessage : 'Failed to update profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const saveTags = async () => {
    // Get current user tags from server
    const currentUserTags = await tagsClient.getUserTags(user.id!);
    const currentTagIds = currentUserTags.map((ut: UserTag) => ut.tagId).filter((id): id is number => id !== undefined);
    const newTagIds = selectedTags
      .filter((t) => isTagValidForUser(t, formData.role, formData.workspaceId))
      .map((tag) => tag.id)
      .filter((id): id is number => id !== undefined);

    // Find tags to add (in new but not in current)
    const tagsToAdd = newTagIds.filter((id) => !currentTagIds.includes(id));

    // Find tags to remove (in current but not in new)
    const tagsToRemove = currentTagIds.filter((id) => !newTagIds.includes(id));

    // Add new tags
    for (const tagId of tagsToAdd) {
      await tagsClient.addUsersToTag(tagId, { userIds: [user.id!] });
    }

    // Remove old tags
    for (const tagId of tagsToRemove) {
      await tagsClient.removeUsersFromTag(tagId, { userIds: [user.id!] });
    }
  };

  const saveGroupMemberships = async () => {
    if (!user.id) return;
    const nextIds = selectedGroups
      .map((g) => g.id)
      .filter((id): id is number => id !== undefined);
    const toAdd = nextIds.filter((id) => !originalGroupIds.includes(id));
    const toRemove = originalGroupIds.filter((id) => !nextIds.includes(id));

    // Per-id try/catch so a single forbidden membership doesn't drop the
    // rest of the diff on the floor. The membership endpoint is one-way
    // (add OR remove a list of users for one group), so we fan out
    // sequentially — the diffs are typically small (≤ a few groups).
    for (const groupId of toAdd) {
      try {
        await groupsClient.addUsersToGroup(groupId, { userIds: [user.id!] });
      } catch (e) {
        console.warn('Failed to add user to group', groupId, e);
      }
    }
    for (const groupId of toRemove) {
      try {
        await groupsClient.removeUsersFromGroup(groupId, { userIds: [user.id!] });
      } catch (e) {
        console.warn('Failed to remove user from group', groupId, e);
      }
    }

    // Re-snapshot so a second Save with no further changes is a no-op
    // diff. (Failed ops above are excluded — if the add 403'd, the
    // original list still lacks that id, so it stays in `toAdd` on
    // the next save.)
    setOriginalGroupIds(nextIds);
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData({ ...passwordData, [field]: value });
    setPasswordErrors({ ...passwordErrors, [field]: '' });
  };

  const validatePassword = () => {
    const newErrors: any = {};

    if (isOwnProfile) {
      if (!passwordData.currentPassword) {
        newErrors.currentPassword = 'Current password is required';
      }
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }

    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordSubmit = async () => {
    if (!validatePassword()) return;

    try {
      setPasswordLoading(true);

      if (isOwnProfile) {
        // Self-change: requires the current password and goes through the
        // dedicated /api/auth/change-password endpoint.
        await authClient.changePassword({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
          confirmPassword: passwordData.confirmPassword,
        });
      } else {
        // Admin reset: bypasses the current-password check; gated server-side
        // by CanManageUser on the target.
        await usersClient.resetUserPassword(user.id!, {
          newPassword: passwordData.newPassword,
          confirmPassword: passwordData.confirmPassword,
        } as any);
      }

      alert(enqueueSnackbar, isOwnProfile ? 'Password changed successfully' : 'Password reset successfully', 'success');

      // Clear password form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setPasswordErrors({});
    } catch (error: any) {
      alert(enqueueSnackbar, error?.response?.data || 'Failed to change password', 'error');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      const newActiveState = !isActive;
      setIsActive(newActiveState);

      // Use dedicated toggle endpoint - does NOT modify role/workspace
      await usersClient.toggleUserStatus(user.id!, newActiveState);

      alert(enqueueSnackbar, `User ${newActiveState ? 'enabled' : 'disabled'} successfully`, 'success');

      onUpdate?.();
    } catch (error: any) {
      // Revert state on error
      setIsActive(!isActive);
      alert(enqueueSnackbar, error?.response?.data || 'Failed to update user status', 'error');
    }
  };

  // Workspace + groups card visibility — derived once so each card's
  // condition is in one place instead of scattered through the JSX.
  // The Access card hides entirely when there's nothing it could host
  // (e.g. learners viewing themselves on a single-tenant account).
  const canSeeRole = canEditRole() || !!availableRoleOptions.find((r) => r.value === user.role);
  const canSeeWorkspace =
    canEditWorkspace() &&
    formData.role !== UserRole.AccountAdmin &&
    formData.role !== UserRole.Admin;
  const canSeeGroups = isAdmin(currentUserRole) && !!formData.workspaceId;
  const showAccessCard = canSeeRole || canSeeWorkspace || canSeeGroups;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 920, pb: 8 }}>
      {/* Read-only banner when a learner is locked out of self-edit.
          Pulled to the top so it sets expectations BEFORE the form. */}
      {isOwnProfile && !canEdit && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            p: 1.25,
            borderRadius: 1,
            bgcolor: 'warning.softBg',
            border: '1px solid #ffe082',
            color: 'warning.softText',
          }}
        >
          <Typography variant="caption" sx={{ color: 'inherit' }}>
            Your workspace admin manages profile fields for this account. Reach out to them if any of the details below need to change.
          </Typography>
        </Box>
      )}

      {/* ── Identity ──────────────────────────────────────────────────
          Photo + name + email + job title. Photo sits inline at the
          top of the card body with a compact upload affordance — no
          longer floats above the form like a disconnected hero. */}
      <ProfileIdentityCard
        user={user}
        formData={formData}
        validationErrors={validationErrors}
        canEdit={canEdit}
        isOwnProfile={isOwnProfile}
        imagePreview={imagePreview}
        resolvedImageUrl={resolvedImageUrl}
        photoSaving={photoSaving}
        onImageSelect={handleImageSelect}
        onImageRemove={handleImageRemove}
        onChange={handleChange}
      />

      {/* ── Access ───────────────────────────────────────────────────
          Role on its own row above; Workspace + Groups paired on the
          row below since Groups are scoped to the workspace and the
          two fields are tightly related (changing workspace clears
          groups). Pairing them visually makes the dependency obvious. */}
      {showAccessCard && (
        <ProfileAccessCard
          user={user}
          formData={formData}
          validationErrors={validationErrors}
          canEdit={canEdit}
          canSeeRole={canSeeRole}
          canEditRole={canEditRole()}
          availableRoleOptions={availableRoleOptions}
          canSeeWorkspace={canSeeWorkspace}
          canSeeGroups={canSeeGroups}
          resolvedWorkspace={resolvedWorkspace}
          onWorkspaceChange={(ws) => {
            setResolvedWorkspace(ws);
            handleChange('workspaceId', ws?.id ?? null);
          }}
          selectedGroups={selectedGroups}
          onGroupsChange={(groups) => setSelectedGroups(groups)}
          groupsLoading={groupsLoading}
          onChange={handleChange}
        />
      )}

      {/* ── Profession + licenses ─────────────────────────────────
          Learner metadata. Hidden when the account hasn't configured
          any professions (nothing to pick) and none is set. The card
          title is enough; the licenses sub-section inside renders its
          own header for the conditional licensed branch. */}
      {(professions.length > 0 || professionId != null) && (
        <SettingsCard title="Professional">
          <LearnerLicensesSection
            professions={professions}
            professionId={professionId}
            licenses={licenses}
            onProfessionChange={setProfessionId}
            onLicensesChange={setLicenses}
            disabled={!canEdit}
          />
        </SettingsCard>
      )}

      {/* ── Reference fields ─────────────────────────────────────
          SSO subjects + HR / payroll identifiers. The Manage fields
          button lives inside the card body (right-aligned above the
          grid) since the inner component owns the dialog wiring. */}
      {showReferenceFields && (
        <SettingsCard
          title="Reference fields"
          subtitle="Used by SSO and external integrations (HR, payroll, scheduling)."
        >
          <UserReferenceFieldsSection
            userId={user?.id}
            values={referenceValues}
            onValuesChange={setReferenceValues}
            canEdit={canEdit}
            canManageSchema={canManageReferenceSchema}
          />
        </SettingsCard>
      )}

      {/* ── Security — Password change/reset ──────────────────────
          Lives in its own card so admins reviewing a learner's
          profile see "credentials live here" as a discrete concern,
          and learners changing their own password don't get the
          form mixed in with the other fields. */}
      {(isOwnProfile || isAdmin(currentUserRole)) && (
        <ProfilePasswordCard
          isOwnProfile={isOwnProfile}
          passwordData={passwordData}
          passwordErrors={passwordErrors}
          passwordLoading={passwordLoading}
          onPasswordChange={handlePasswordChange}
          onSubmit={handlePasswordSubmit}
        />
      )}

      {/* Sticky save bar for the profile-fields save. Pinned to the
          bottom of MainLayout's scroll container so admins on long
          profiles (with reference fields, licenses, etc.) don't have
          to scroll back down to commit changes. Hidden when the
          viewer can't edit anything in the form. */}
      {canEdit && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            mx: -4,
            mt: 1,
            px: 4,
            py: 1.5,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(6px)',
            borderTop: '1px solid',
            borderColor: 'divider',
            zIndex: 5,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={loading}
            sx={{
              px: 3,
              '&.Mui-disabled': { color: '#fff', opacity: 0.65 },
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Save changes'}
          </Button>
        </Box>
      )}
    </Box>
  );
}
